import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  getAbsentStudentsRequest,
  livevideoReportRequest,
  notifyForAbsenceRequest,
} from "../../../modules/admin-modules/liveVideos/Actions";
import { getAllLiveVideosRequest } from "../../../modules/superadmin-modules/live-videos/Actions";

const LiveVideosReports = () => {
  const dispatch = useDispatch();
  const [selectedLiveVideo, setSelectedLiveVideo] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const { liveVideos } = useSelector((state) => state.superadminLiveVideos);
  const { liveVideosReport, absentStudents, isLoading } = useSelector(
    (state) => state.adminLiveVideos
  );

  const liveVideosOptions = liveVideos?.items?.map((video) => ({
    value: video.id,
    label: video.title,
  }));

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "white",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  useEffect(() => {
    dispatch(getAllLiveVideosRequest());
  }, [dispatch]);

  useEffect(() => {
    if (selectedLiveVideo) {
      if (activeTab === 0) {
        // Get attended students
        dispatch(livevideoReportRequest(selectedLiveVideo));
      } else {
        // Get absent students
        dispatch(getAbsentStudentsRequest(selectedLiveVideo));
      }
    }
  }, [selectedLiveVideo, activeTab, dispatch]);

  const handleLiveVideoChange = (selectedOption) => {
    setSelectedLiveVideo(selectedOption?.value || "");
    setSelectedStudents([]);
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = (studentIds) => {
    setSelectedStudents(studentIds);
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const getCurrentStudents = () => {
    if (activeTab === 0) {
      return Array.isArray(liveVideosReport) ? liveVideosReport : [];
    } else {
      return Array.isArray(absentStudents) ? absentStudents : [];
    }
  };

  const currentStudents = getCurrentStudents();
  const currentStudentIds = currentStudents.map((student) => student.id);

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Header with Notification Button */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">تقارير الفيديوهات المباشرة</Heading>
          {activeTab === 1 && (
            <Button
              colorScheme="blue"
              onClick={() => {
                if (selectedStudents.length > 0) {
                  dispatch(
                    notifyForAbsenceRequest(
                      {
                        zoom_id: selectedLiveVideo,
                        students_id: selectedStudents,
                      },
                      () => {
                        // Clear selected students after successful notification
                        setSelectedStudents([]);
                      }
                    )
                  );
                }
              }}
              isDisabled={
                selectedStudents.length === 0 || !selectedLiveVideo || isLoading
              }
            >
              إرسال إشعار للغياب ({selectedStudents.length})
              {isLoading && <Spinner size="sm" ml={2} />}
            </Button>
          )}
        </Flex>

        {/* Live Video Selection */}
        <HStack>
          <Text fontWeight="bold" mb={2}>
            اختر الفيديو المباشر:
          </Text>
          <Select
            options={liveVideosOptions}
            onChange={handleLiveVideoChange}
            styles={colourStyles}
            placeholder="اختر الفيديو المباشر"
            isClearable
          />
        </HStack>

        {/* Reports Tabs */}
        <Tabs variant="enclosed" onChange={(index) => setActiveTab(index)}>
          <TabList>
            <Tab>الحضور</Tab>
            <Tab>الغياب</Tab>
          </TabList>

          <TabPanels>
            {/* Attended Students Tab */}
            <TabPanel>
              <Box>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md">قائمة الحضور</Heading>
                </Flex>

                {currentStudents.length > 0 ? (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>اسم الطالب</Th>
                        <Th>البريد الإلكتروني</Th>
                        <Th>رقم الهاتف</Th>
                        <Th>الحالة</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentStudents.map((student) => (
                        <Tr key={student.id}>
                          <Td>{student.name}</Td>
                          <Td>{student.email}</Td>
                          <Td>{student.phone || "غير متوفر"}</Td>
                          <Td>
                            <Badge colorScheme="green">حاضر</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Text textAlign="center" color="gray.500" py={8}>
                    {selectedLiveVideo
                      ? "لا يوجد طلاب حاضرون"
                      : "اختر فيديو مباشر لعرض الحضور"}
                  </Text>
                )}
              </Box>
            </TabPanel>

            {/* Absent Students Tab */}
            <TabPanel>
              <Box>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md">قائمة الغياب</Heading>
                  <HStack>
                    <Button
                      size="sm"
                      onClick={() => handleSelectAll(currentStudentIds)}
                      isDisabled={currentStudents.length === 0}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleDeselectAll}
                      isDisabled={selectedStudents.length === 0}
                    >
                      إلغاء التحديد
                    </Button>
                  </HStack>
                </Flex>

                {currentStudents.length > 0 ? (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>تحديد</Th>
                        <Th>اسم الطالب</Th>
                        <Th>البريد الإلكتروني</Th>
                        <Th>رقم الهاتف</Th>
                        <Th>الحالة</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentStudents.map((student) => (
                        <Tr key={student.id}>
                          <Td>
                            <Checkbox
                              isChecked={selectedStudents.includes(student.id)}
                              onChange={() => handleStudentToggle(student.id)}
                            />
                          </Td>
                          <Td>{student.name}</Td>
                          <Td>{student.email}</Td>
                          <Td>{student.phone || "غير متوفر"}</Td>
                          <Td>
                            <Badge colorScheme="red">غائب</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Text textAlign="center" color="gray.500" py={8}>
                    {selectedLiveVideo
                      ? "لا يوجد طلاب غائبون"
                      : "اختر فيديو مباشر لعرض الغياب"}
                  </Text>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Summary */}
        {activeTab === 1 && selectedStudents.length > 0 && (
          <Box p={4} bg="blue.50" borderRadius="md">
            <Text fontWeight="bold">
              تم تحديد {selectedStudents.length} طالب
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default LiveVideosReports;
